import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';

import { loanSlotsForLoan } from '@/components/agreements/boardSlots';
import {
    AgreementSealingProvider,
    useAgreementSealing,
} from '@/components/agreements/useAgreementSealing';
import { useContractAgreements } from '@/components/agreements/useContractAgreements';
import { supabase } from '@/integrations/supabase/client';
import { captureError } from '@/lib/logger';

import { focusJumpTarget, useDossierChrome } from '@/components/contracts/setup/dossier/useDossierChrome';
import CommitBar from '@/components/contracts/setup/inline/CommitBar';
import Manifest from '@/components/contracts/setup/manifest/Manifest';
import NarratorLine from '@/components/contracts/setup/manifest/NarratorLine';
import { journeyLabel } from '@/components/contracts/setup/manifest/manifestView';
import { areaPath, type SetupArea } from '@/components/contracts/setup/steps';
import {
    advanceInvalidationKeys,
    createAdvance,
    updateAdvance,
    validateDraft,
} from '@/components/contracts/setup/advance/advanceCommit';
import { resolveAdvanceDeepLink } from '@/components/contracts/setup/advance/advanceDeepLink';
import {
    advanceDraftOf,
    advanceAreaDirty,
    advanceFormWith,
    AREA_FIELDS,
    type AdvanceFormValues,
} from '@/components/contracts/setup/advance/advanceForm';
import type { AdvanceRow, AdvanceRowId } from '@/components/contracts/setup/advance/advanceState';
import {
    ADVANCE_AREAS,
    ADVANCE_AREA_LABELS,
    advanceAreaPath,
    parseLoanParam,
    resolveAdvanceArea,
    type AdvanceArea,
} from '@/components/contracts/setup/advance/advanceSteps';
import { useAdvanceState } from '@/components/contracts/setup/advance/useAdvanceState';
import AdvanceAgreementArea from '@/components/contracts/setup/advance/areas/AdvanceAgreementArea';
import DisbursementArea from '@/components/contracts/setup/advance/areas/DisbursementArea';
import RepaymentArea from '@/components/contracts/setup/advance/areas/RepaymentArea';
import CreateAdvanceConfirm from '@/components/contracts/setup/advance/manifest/CreateAdvanceConfirm';
import type { SetupProgress } from '@/components/contracts/setup/setupState';

/** What the rail's draft mark says for a field an edit EMPTIED — the mark needs a word to draw. */
const CLEARED = 'cleared';

/**
 * The advance setup dossier — the contract dossier's shape over one `loans` row.
 *
 * ONE ROUTE, ONE SUBJECT, THREE AREAS. `/dashboard/contracts/:id/advances/:loanId/:area?` serves
 * both the create flow (`:loanId` is the literal `new`) and every existing advance, and the area
 * lives in the PATH for the reason it does on the contract dossier: a merge-field pre-flight links
 * to a place, a half-finished advance is bookmarkable, and Back walks the areas rather than the
 * history of one screen. A bare path resolves to a real area and replaces itself once the reads
 * have landed, through a ref latch that fires exactly once — so a background refetch can never move
 * an admin off the area they are typing into.
 *
 * IT IS THE SAME SHELL AND NOT A COPY OF IT. `Manifest`, `NarratorLine`, `useDossierChrome`, the
 * `.setup-dossier` grid and the vaul journey sheet are the contract dossier's own, generic over the
 * row and area vocabularies since the shell refactor. What differs is the SUBJECT, and two things
 * follow from it:
 *
 * - THERE IS NO LEDGER AND NO ACTIVATION GATE. An advance has no statistics column to keep and no
 *   ceremony to gate — `.sd-no-ledger` drops the grid's third track and gives the canvas back the
 *   880px measure, because a three-track grid with nothing in the third leaves a 278px dead column.
 * - THE MANIFEST FOOTER IS NEVER THE COMMIT. `.sd-manifest-foot` is `display: none` below 1024px,
 *   so a create button living there would be invisible on a phone. The footer carries the one-line
 *   state; the writes happen on a sticky `CommitBar` at the foot of the canvas that owns them.
 *
 * THE SEALING LATCH IS PAGE-WIDE, keyed off the DOCUMENTS rather than off any area's slots —
 * `ContractSetup.tsx`'s arrangement, for its reason: a COMPLETED document that no mounted canvas
 * claims would otherwise be sealed by nobody at all, and two latches racing each other land on
 * `AGREEMENT_FINALIZE_IN_PROGRESS`, which the loser reports as a failed seal needing a human.
 *
 * `junctionScope: 'all'` IS THE ONLY SCOPE THAT MINTS A LOAN SLOT [D-B17]. The setup board reads no
 * `loans` at all, deliberately — a standing advance is not part of setting a contract up — so this
 * page asks for the board Contract Insights asks for, which is the board this advance has a row on.
 *
 * There is no new permission: the route sits under `/dashboard/contracts/...`, and `canAccessRoute`
 * matches on the first path segment.
 */
export default function AdvanceSetup() {
    const { contractId: contractIdParam, loanId: loanIdParam, area: areaParam } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const contractId = parseInt(contractIdParam || '0', 10);
    /** `parseInt('new')` is `NaN`, and this is why nothing here ever calls it — see `parseLoanParam`. */
    const loanSubject = parseLoanParam(loanIdParam);
    const routeArea = resolveAdvanceArea(areaParam);

    const { rootRef } = useDossierChrome<HTMLDivElement>();
    const [activeRowId, setActiveRowId] = useState<AdvanceRowId | null>(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [edits, setEdits] = useState<Partial<AdvanceFormValues>>({});

    const loanId: number | 'new' = loanSubject ?? 'new';
    const isNew = loanId === 'new';
    const mode: 'create' | 'edit' = isNew ? 'create' : 'edit';

    /**
     * THE ADDRESS NAMES SOMETHING THAT IS NOT AN ADVANCE — one answer, at either depth.
     *
     * `loanId` falls back to `'new'` for a segment `parseLoanParam` refuses, which is right for the
     * word `new` and wrong for `abc`. The error screen below catches it, but the landing latch runs
     * first and had only `routeArea` to stop it: `/advances/abc/disbursement` showed the error while
     * `/advances/abc` replaced itself into the create flow, with the bad id gone from the bar.
     */
    const badSubject =
        loanSubject === null && loanIdParam !== undefined && loanIdParam.toLowerCase() !== 'new';

    const agreements = useContractAgreements({ contractId, junctionScope: 'all' });

    const sealing = useAgreementSealing({
        documents: agreements.documents,
        onRefresh: agreements.refreshBoard,
    });

    /**
     * Unsaved edits, by rail row — what draws the blue "draft" mark in the manifest.
     *
     * NEVER COUNTED AS PROGRESS. A typed figure is not a saved one, and a rail that graded the form
     * would tell an admin an advance was ready while nothing had been written.
     *
     * PRESENCE, NOT TRUTHINESS — AND CLEARING IS A WORD. An edit that EMPTIES a box is an edit:
     * `advanceAreaDirty` counts it, so the commit bar stands over the page while the rail would
     * otherwise still show the server's value with no draft mark, the two halves disagreeing about
     * whether anything is pending. The mark is drawn from a non-empty string, so a cleared field
     * needs one of its own rather than the empty box it holds.
     */
    const drafts = useMemo<Partial<Record<AdvanceRowId, string>>>(() => {
        const map: Partial<Record<AdvanceRowId, string>> = {};
        if (edits.transactionId !== undefined) {
            map['adv-transaction'] =
                edits.transactionId === null ? CLEARED : String(edits.transactionId);
        }
        if (edits.interestRate !== undefined) map['adv-rate'] = edits.interestRate || CLEARED;
        if (edits.purpose !== undefined) map['adv-purpose'] = edits.purpose || CLEARED;
        if (edits.weeklyAmount !== undefined) map['adv-weekly'] = edits.weeklyAmount || CLEARED;
        if (edits.firstStartsAt !== undefined) map['adv-first'] = edits.firstStartsAt || CLEARED;
        return map;
    }, [edits]);

    const { state, snapshot, stateReady, landingArea, existing, forward, saved } = useAdvanceState({
        contractId,
        loanId,
        agreements,
        drafts,
    });

    const form = useMemo(() => advanceFormWith(saved, edits), [saved, edits]);
    const draft = useMemo(() => advanceDraftOf(form), [form]);
    const refusals = useMemo(
        () => validateDraft(draft, mode, existing),
        [draft, mode, existing],
    );

    const goToArea = useCallback(
        (area: AdvanceArea) => navigate(advanceAreaPath(contractId, loanId, area)),
        [navigate, contractId, loanId],
    );

    /**
     * A ROUTE-LESS ENTRY RESOLVES ITSELF, ONCE — `ContractSetup.tsx:152-160`'s latch.
     *
     * It waits for every read to answer, fires exactly once through a ref, and is an unconditional
     * no-op whenever an area is already in the URL.
     */
    const landed = useRef(false);
    useEffect(() => {
        if (routeArea) return;
        if (badSubject) return;
        if (!stateReady) return;
        if (landed.current) return;
        landed.current = true;
        navigate(advanceAreaPath(contractId, loanId, landingArea), { replace: true });
    }, [routeArea, badSubject, stateReady, landingArea, contractId, loanId, navigate]);

    /** A different advance is a different subject: nothing typed about the last one carries over. */
    useEffect(() => {
        setEdits({});
        setActiveRowId(null);
    }, [loanId]);

    const activeArea = routeArea ?? landingArea;
    const areaIndex = ADVANCE_AREAS.findIndex((candidate) => candidate.key === activeArea);
    const area = ADVANCE_AREAS[areaIndex >= 0 ? areaIndex : 0];
    const previousArea = areaIndex > 0 ? ADVANCE_AREAS[areaIndex - 1] : null;
    const nextArea =
        areaIndex >= 0 && areaIndex < ADVANCE_AREAS.length - 1 ? ADVANCE_AREAS[areaIndex + 1] : null;

    const selectRow = useCallback(
        (row: AdvanceRow) => {
            setActiveRowId(row.id);
            setSheetOpen(false);
            if (row.jump.area !== activeArea) goToArea(row.jump.area);
            focusJumpTarget(rootRef.current, row.jump.block, row.jump.focus);
        },
        [activeArea, goToArea, rootRef],
    );

    useEffect(() => {
        if (!activeRowId) return;
        const selected = state.rows.find((candidate) => candidate.id === activeRowId);
        if (selected && selected.area !== activeArea) setActiveRowId(null);
    }, [activeArea, activeRowId, state.rows]);

    /**
     * WHERE A PRE-FLIGHT OR A LOCKED SLOT SENDS THE ADMIN, AND IT IS TOTAL OVER THE VOCABULARY.
     *
     * `AgreementDocumentRow` draws "Resolve in …" over a blocked slot and `MergePreflight` draws
     * one per unresolved merge field; both call this, and loan v7 quotes eighteen keys of which
     * only six are edited on this dossier at all. The other twelve are the contract's — so they
     * leave the page, on the route `ManageContractSheet`'s "Open setup" already uses. Handed a key
     * neither vocabulary knows, `resolveAdvanceDeepLink` still composes the bare setup path rather
     * than a URL made of a word the router has no page for.
     */
    const onDeepLink = useCallback(
        (value: string) => {
            const target = resolveAdvanceDeepLink(contractId, value);
            if (target.kind === 'advance') {
                goToArea(target.area);
                return;
            }
            navigate(target.path);
        },
        [contractId, goToArea, navigate],
    );

    const onGoToSetupArea = useCallback(
        (setupArea: SetupArea) => navigate(areaPath(contractId, setupArea)),
        [contractId, navigate],
    );

    const invalidate = useCallback(() => {
        for (const key of advanceInvalidationKeys(contractId)) {
            queryClient.invalidateQueries({ queryKey: key });
        }
        agreements.refreshBoard();
    }, [queryClient, contractId, agreements]);

    /**
     * THE CREATE — refused before it is attempted, proof-read before it is confirmed.
     *
     * `createAdvance` re-runs `validateDraft` and rolls the `loans` row back if the instalment
     * insert fails, so the two round trips cannot leave an advance that silently bills nothing.
     * On success the page becomes the advance's own dossier, on its Agreement area, with `replace`
     * so Back does not walk into a create flow whose subject now exists.
     */
    const create = useMutation({
        mutationFn: () => createAdvance(supabase, { ...draft, contractId }),
        onSuccess: ({ id }) => {
            toast.success('Advance created');
            setEdits({});
            setConfirmOpen(false);
            invalidate();
            navigate(advanceAreaPath(contractId, id, 'agreement'), { replace: true });
        },
        onError: (error: Error) => {
            captureError(error);
            toast.error(error.message || 'Failed to create the advance');
        },
    });

    /**
     * THE EDIT — one bar per area, both writing through the one writer.
     *
     * `updateAdvance` writes the `loans` row and then rewrites the schedule to exactly ONE rate row,
     * writing before it deletes: an UPDATE where the date is unchanged, an INSERT where it moved,
     * and in both cases a delete of every OTHER row of the loan — scoped to the loan rather than to
     * the dates this client read, so a concurrent save cannot leave two rows behind. A repayment
     * pair that has not moved plans nothing at all, so a purpose correction committed from the
     * Disbursement bar never rewrites a schedule nobody touched.
     */
    const save = useMutation({
        mutationFn: () => {
            if (typeof loanId !== 'number') throw new Error('This advance has not been created yet.');
            return updateAdvance(supabase, loanId, draft, existing);
        },
        onSuccess: () => {
            toast.success('Advance updated');
            setEdits({});
            invalidate();
        },
        onError: (error: Error) => {
            captureError(error);
            toast.error(error.message || 'Failed to update the advance');
        },
    });

    const onChange = useCallback(
        (updates: Partial<AdvanceFormValues>) =>
            setEdits((current) => ({ ...current, ...updates })),
        [],
    );

    const discardArea = useCallback((target: AdvanceArea) => {
        setEdits((current) => {
            const next = { ...current };
            for (const field of AREA_FIELDS[target]) delete next[field];
            return next;
        });
    }, []);

    /**
     * What the create bar says while the writer would refuse.
     *
     * IT NAMES ONE AND COUNTS THE REST, and it names WHERE that one lives: the refusal list is in
     * reading order across two areas, and an admin told only "something is missing" on the
     * repayment canvas would go looking for it there.
     */
    const createHint = (() => {
        if (refusals.length === 0) {
            return 'Writes the advance and its repayment schedule together.';
        }
        const [first] = refusals;
        const where = ADVANCE_AREA_LABELS[first.area];
        const rest = refusals.length - 1;
        return rest > 0
            ? `${first.message} (${where}) · ${rest} more to fill in`
            : `${first.message} (${where})`;
    })();

    const areaCommit = (target: AdvanceArea) => {
        // NO BAR WHILE THE BOARD IS REFUSED, IN EITHER MODE. The page's rate arithmetic — the
        // forward weekly total the "adds up to" panel states, and the supersede the repayment
        // rewrite plans — is composed from THIS contract's other advances, which are read off the
        // board's subjects. After a refused read that list is empty rather than absent, so a create
        // would be confirmed against an understated total and an edit would plan an insert with no
        // supersede. The repayment canvas says why in its own words; the commit simply is not there.
        if (agreements.failed) return null;
        if (mode === 'create') {
            // ONE CREATE BAR, AND IT LIVES ON THE REPAYMENT CANVAS. The advance and its schedule
            // are written together, and the repayment area is the last of the two that states
            // figures — so the commit stands under the last thing typed rather than halfway up.
            if (target !== 'repayment') return null;
            return (
                <CommitBar
                    saving={create.isPending}
                    saveLabel="Create advance"
                    hint={createHint}
                    disabled={refusals.length > 0}
                    onSave={() => setConfirmOpen(true)}
                />
            );
        }
        // AND NO BAR UNTIL EVERY READ THIS ADVANCE IS JUDGED AGAINST HAS ANSWERED, which is the
        // watermark's own direction applied to the commit. `frozen` is `!!slot?.document?.frozen_at`
        // and `existing.instalments` comes off the board's subjects, so while the board is still
        // loading BOTH read as "nothing there": a frozen advance would offer a save because the
        // freeze had not arrived, and a repayment save would plan an insert with no supersede
        // because the rate row it should be correcting was not in hand. The refused case is the
        // guard above, which holds for a create too.
        if (!stateReady) return null;
        if (snapshot.frozen) return null;
        if (!advanceAreaDirty(target, saved, edits)) return null;
        const areaRefusal = refusals.find((refusal) => refusal.area === target) ?? refusals[0] ?? null;
        return (
            <CommitBar
                saving={save.isPending}
                hint={
                    areaRefusal
                        ? `${areaRefusal.message} (${ADVANCE_AREA_LABELS[areaRefusal.area]})`
                        : 'Unsaved changes on this block.'
                }
                disabled={refusals.length > 0}
                onSave={() => save.mutate()}
                onDiscard={() => discardArea(target)}
            />
        );
    };

    const areaContent = () => {
        /*
         * THE SPINNER IS ABOUT THE READS, NOT ABOUT THE URL. Gating it on a missing area segment
         * covered only the landing redirect — and every OTHER way in carries an area already: a
         * bookmark, browser Back, the Advances card's CTA, the landing latch's own target. Until
         * the board's subjects land `subjectLoan` is null, so the repayment canvas would state
         * "Settled at completion — this advance predates the weekly-schedule rule" [D-B54] about an
         * advance that is on a weekly schedule, and the rail would draw the D-B55 padlock. Both
         * correct themselves a tick later, which is precisely the tick in which an admin reads them.
         *
         * THE REFUSED READ IS THE OTHER HALF AND IT NEVER CORRECTS ITSELF, so it is not the
         * spinner's to hold: `agreements.failed` resolves the repayment pair to `'board-unreadable'`
         * in `advanceLegacyReason`, which is what the canvas and the rail both say out loud.
         */
        if (!stateReady) {
            return (
                <div className="flex justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            );
        }

        switch (area.key) {
            case 'disbursement':
                return (
                    <DisbursementArea
                        mode={mode}
                        frozen={snapshot.frozen}
                        form={form}
                        onChange={onChange}
                        commit={areaCommit('disbursement')}
                    />
                );
            case 'repayment':
                return (
                    <RepaymentArea
                        mode={mode}
                        frozen={snapshot.frozen}
                        legacyNoSchedule={snapshot.legacyNoSchedule}
                        form={form}
                        onChange={onChange}
                        forward={forward}
                        commit={areaCommit('repayment')}
                    />
                );
            case 'agreement':
                return (
                    <AdvanceAgreementArea
                        loanId={loanId}
                        agreements={agreements}
                        slots={isNew ? [] : loanSlotsForLoan(agreements.slots, loanId)}
                        driver={snapshot.driver}
                        vehicle={snapshot.vehicle}
                        hpReference={snapshot.hpReference}
                        hpDriverSigned={snapshot.hpDriverSigned}
                        onDeepLink={onDeepLink}
                        onGoToSetupArea={onGoToSetupArea}
                    />
                );
            default:
                return null;
        }
    };

    /**
     * The advance's name, in the order an admin would say it.
     *
     * THE PURPOSE IS THE NAME WHEN THERE IS ONE, because `Advance #237` identifies a row and
     * `Engine rebuild` identifies the thing the driver borrowed for. The id is the fallback rather
     * than the title for the same reason the contract dossier leads with the driver's name.
     */
    const heading = isNew
        ? 'New advance'
        : snapshot.loan?.purpose?.trim() || `Advance #${loanId}`;

    const footerNode = <div className="sd-gate-note">{footerNote(state.progress)}</div>;

    if (badSubject) {
        return (
            <div className="space-y-4 px-4 pt-6 sm:px-6 lg:px-8">
                <p className="text-lg">That is not an advance.</p>
                <p className="text-sm text-muted-foreground">
                    The address names <code>{loanIdParam}</code>, and an advance is identified by a
                    number (or by the word <code>new</code>).
                </p>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/dashboard/contracts/${contractId}?tab=loans`)}
                >
                    Back to Advances
                </Button>
            </div>
        );
    }

    return (
        <AgreementSealingProvider value={sealing}>
            <div className="setup-dossier sd-no-ledger -m-4 lg:-m-6" ref={rootRef}>
                <Manifest
                    state={state}
                    activeArea={activeArea}
                    activeRowId={activeRowId}
                    onSelectRow={selectRow}
                    footer={footerNode}
                    head={
                        <header className="sd-hdr">
                            <div className="sd-hdr-top">
                                <h1 className="min-w-0 text-4xl font-semibold tracking-tight">
                                    {heading}
                                </h1>
                                {/* Below 1024px the journey is a sheet, and this is the way into it. */}
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="ml-auto h-8 flex-none lg:hidden"
                                    aria-expanded={sheetOpen}
                                    onClick={() => setSheetOpen(true)}
                                >
                                    {journeyLabel(state.progress)}
                                </Button>
                            </div>

                            <NarratorLine
                                state={state}
                                onJump={selectRow}
                                /* NO ACTIVATION AND NO RECEIPT ON THIS RAIL. `computeAdvanceState`
                                   emits neither intent — an advance has no ceremony to open and no
                                   receipt to re-open — so these two can never be reached, and they
                                   are wired rather than stubbed silently so that an action added to
                                   the advance narrator without a handler fails loudly here. */
                                onActivate={() => undefined}
                                onOpenReceipt={() => undefined}
                            />

                            {/* THE WAY BACK IS A LINK IN THE HEAD, and it lands on the tab the
                                advance was opened from — which is why that tab is in the URL. */}
                            <div className="sd-hdr-actions">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-2"
                                    onClick={() =>
                                        navigate(`/dashboard/contracts/${contractId}?tab=loans`)
                                    }
                                >
                                    Back to Advances
                                </Button>
                            </div>
                        </header>
                    }
                />

                <main className="sd-canvas-wrap">
                    <div className="sd-canvas">
                        <h2 className="sd-area-h">{area.label}</h2>
                        <p className="sd-area-q">{area.question}</p>

                        {areaContent()}

                        <nav className="sd-area-foot" aria-label="Areas">
                            {previousArea ? (
                                <button
                                    type="button"
                                    className="sd-af-btn"
                                    onClick={() => goToArea(previousArea.key)}
                                >
                                    <span>Previous</span>
                                    {previousArea.label}
                                </button>
                            ) : (
                                <span />
                            )}
                            {nextArea && (
                                <button
                                    type="button"
                                    className="sd-af-btn sd-af-btn-right"
                                    onClick={() => goToArea(nextArea.key)}
                                >
                                    <span>Next</span>
                                    {nextArea.label}
                                </button>
                            )}
                        </nav>
                    </div>
                </main>

                {/* Below 1024px the manifest is this sheet, behind the persistent Journey button. */}
                <Drawer open={sheetOpen} onOpenChange={setSheetOpen}>
                    <DrawerContent className="sd-tokens h-[78vh] max-h-[78vh]">
                        <DrawerTitle className="sr-only">Journey manifest</DrawerTitle>
                        <Manifest
                            state={state}
                            activeArea={activeArea}
                            activeRowId={activeRowId}
                            onSelectRow={selectRow}
                            footer={footerNode}
                            variant="sheet"
                        />
                    </DrawerContent>
                </Drawer>

                <CreateAdvanceConfirm
                    open={confirmOpen}
                    onOpenChange={setConfirmOpen}
                    draft={draft}
                    snapshot={snapshot}
                    pending={create.isPending}
                    blocked={refusals.length > 0}
                    onConfirm={() => create.mutate()}
                />
            </div>
        </AgreementSealingProvider>
    );
}

/**
 * The manifest footer's ONE LINE, and it is a state and never a control.
 *
 * `.sd-manifest-foot` is `display: none` below 1024px, so anything that WRITES cannot live here:
 * the create button would simply not exist on a phone. What belongs is the same count the journey
 * trigger carries, said in full where there is room for it.
 */
function footerNote(progress: SetupProgress): string {
    if (progress.outstanding === 0) return 'Nothing outstanding on this advance.';
    const parts = [`${progress.outstanding} open`];
    if (progress.needYou > 0) parts.push(`${progress.needYou} need you`);
    if (progress.waitingOnOthers > 0) parts.push(`${progress.waitingOnOthers} waiting on others`);
    return parts.join(' · ');
}
